import { Grid, MenuItem } from "@mui/material";
import { FormTextField } from "components/atoms";
import { FormContainer, FormContent } from "components/molecules";
import CommonModal, { ICommonModal } from "components/molecules/CommonModal";
import { useFormik, useFormikContext } from "formik";
import { AlertHelper } from "helpers";
import { EAlertType } from "helpers/alertHelper";
import {
  EQualityCheckingConditionType,
  IApiResponse,
  IProductGroup,
  IQualityCondition,
} from "model";
import { useMemo, useState } from "react";
import { QualityConditionApiService } from "services";
import { I18nTranslator, translations } from "utils/i18n";
import * as yup from "yup";
import "themes/hide-increment-number-input.css";

export const QualityConditionDetailModal = (
  props: Pick<ICommonModal, "open" | "handleClose"> & {
    qualityCondition?: IQualityCondition;
    groupId: string;
    onSuccess: (qualityCondition: IQualityCondition) => void;
  }
) => {
  const parentForm = useFormikContext<
    IProductGroup & { qualityConditions?: IQualityCondition[] }
  >();
  const isAdd = useMemo(() => {
    return !props.qualityCondition;
  }, [props.qualityCondition]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const DEFAULT_NEW_QUALITY_CONDITION = useMemo<Partial<IQualityCondition>>(
    () => {
      return props.qualityCondition
        ? {
            ...props.qualityCondition,
            imageRequired: props.qualityCondition.imageRequired ? 1 : 0,
          }
        : {
            groupID: props.groupId,
            name: "",
            type: undefined,
            displayOrder: parentForm.values.qualityConditions?.length
              ? parentForm.values.qualityConditions?.length + 1
              : 1,
            imageRequired: 1,
          };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.qualityCondition, props.open]
  );
  const formik = useFormik<Partial<IQualityCondition>>({
    initialValues: DEFAULT_NEW_QUALITY_CONDITION,
    enableReinitialize: true,
    validationSchema: QualityConditionValidationSchema,
    onSubmit: (values: Partial<IQualityCondition>) => {
      if (isAdd) {
        createQualityCondition(values);
      } else {
        updateQualityCondition(values as IQualityCondition);
      }
    },
  });

  const createQualityCondition = async (
    request: Partial<IQualityCondition>
  ) => {
    try {
      setIsLoading(true);
      const result = (await QualityConditionApiService.addQualityCondition({
        ...request,
        imageRequired: !!request.imageRequired,
      })) as IApiResponse<IQualityCondition>;
      if (result.succeeded) {
        // formik.setValues(result.data!);
        formik.resetForm({ values: DEFAULT_NEW_QUALITY_CONDITION });
        AlertHelper.showAlert(
          I18nTranslator(translations.productGroup.addQualityConditionSuccess),
          EAlertType.SUCCESS
        );
        props.onSuccess(result.data!);
      } else {
        AlertHelper.showAlert(
          result.message[0]?.Text ||
            I18nTranslator(translations.errorMessage.unknownError),
          EAlertType.ERROR
        );
        console.error("error", result);
      }
    } catch (error) {
      console.error("error", error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateQualityCondition = async (request: IQualityCondition) => {
    try {
      setIsLoading(true);
      const result = (await QualityConditionApiService.editQualityCondition({
        ...request,
        imageRequired: !!request.imageRequired,
      })) as IApiResponse<IQualityCondition>;
      if (result.succeeded) {
        AlertHelper.showAlert(
          I18nTranslator(
            translations.productGroup.updateQualityConditionSuccess
          ),
          EAlertType.SUCCESS
        );
        props.onSuccess(result.data!);
      } else {
        console.error("error", result);
      }
    } catch (error) {
      console.error("error", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <CommonModal
      {...props}
      title={
        isAdd
          ? I18nTranslator(translations.productGroup.addQualityCondition)
          : I18nTranslator(translations.productGroup.udpateQualityCondition)
      }
    >
      <FormContainer formik={formik} loading={isLoading} isAdd={isAdd}>
        <FormContent>
          <Grid xs={12} item>
            <FormTextField
              label={I18nTranslator(translations.name)}
              id="name"
              required
            />
          </Grid>
          <Grid xs={12} item>
            <FormTextField
              label={I18nTranslator(translations.productGroup.enName)}
              id="nameEN"
              required
            />
          </Grid>
          <Grid xs={12} item>
            <FormTextField
              label={I18nTranslator(
                translations.productGroup.conditionValueType
              )}
              id="type"
              required
              select
            >
              {QUALITY_TYPES.map((e) => (
                <MenuItem value={e.value}>{I18nTranslator(e.name)}</MenuItem>
              ))}
            </FormTextField>
          </Grid>
          <Grid xs={12} item>
            <FormTextField
              label={I18nTranslator(translations.productGroup.displayOrder)}
              id="displayOrder"
              inputProps={{ inputMode: "numeric" }}
              type="number"
            />
          </Grid>
          <Grid xs={12} item>
            <FormTextField
              label={I18nTranslator(translations.productGroup.imageRequired)}
              id="imageRequired"
              inputProps={{ inputMode: "numeric" }}
              select
            >
              <MenuItem value={1}>
                {I18nTranslator(translations.productGroup.required)}
              </MenuItem>
              <MenuItem value={0}>
                {I18nTranslator(translations.productGroup.notRequired)}
              </MenuItem>
            </FormTextField>
          </Grid>
        </FormContent>
      </FormContainer>
    </CommonModal>
  );
};

const QualityConditionValidationSchema = yup
  .object()
  .shape<
    Record<
      keyof Pick<IQualityCondition, "name" | "displayOrder" | "type">,
      yup.AnySchema
    >
  >({
    name: yup.string().required(translations.yupError.required),
    displayOrder: yup
      .number()
      .typeError(translations.yupError.formatNotValid)
      .integer(translations.yupError.formatNotValid)
      .min(1, translations.yupError.mustEqualOrGreaterThanOne),
    type: yup.number().required(translations.yupError.required),
  });

type TQualityType = {
  name: string;
  value: EQualityCheckingConditionType;
};
const QUALITY_TYPES: TQualityType[] = [
  {
    name: translations.productGroup.predefined,
    value: EQualityCheckingConditionType.Predefined,
  },
  {
    name: translations.productGroup.inputText,
    value: EQualityCheckingConditionType.InputText,
  },
];
